<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Search your EMR</h1>
      <p class="subtitle">
        <template v-if="patient">
          Find the EMR record for
          <strong>{{ patientName }}</strong>
          <template v-if="patient.dateOfBirth"> (DOB {{ formatUtils.formatDate(patient.dateOfBirth) }})</template>
          and connect it to this record.
        </template>
        <template v-else>Find the matching patient in your EMR and connect it to this record.</template>
      </p>
    </div>

    <div class="modal-card-body">
      <SearchBar :modelValue="query" @update:modelValue="handleQueryChange" placeholder="Search EMR patients..." />

      <div class="results">
        <div v-if="loading" class="results-state">Searching your EMR…</div>
        <div v-else-if="error" class="results-state error">{{ error }}</div>
        <div v-else-if="results.length === 0" class="results-state">No matching patients found</div>
        <button
          v-else
          v-for="result in results"
          :key="result.emrIntegrationId"
          class="result-item"
          @click="$emit('connect', result)"
        >
          <div class="result-main">
            <span class="result-name">{{ result.firstName }} {{ result.lastName }}</span>
            <span class="result-dob">DOB {{ formatUtils.formatDate(result.dateOfBirth) }}</span>
            <span v-if="isExactMatch(result)" class="result-match-tag">Exact match</span>
          </div>
          <span class="result-id">{{ result.emrIntegrationId }}</span>
        </button>
      </div>
    </div>

    <div class="modal-card-footer">
      <button class="button form-button close-on-click">Cancel</button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import SearchBar from '@/components/common/SearchBar.vue';
import { searchEmrPatients } from '@/libs/extensionBridge';
import { installEmrStub } from '@/libs/emrExtensionStub';
import * as formatUtils from '@/utils/formatters';

export default {
  name: 'ConnectEmrModal',
  components: { CloseIcon, SearchBar },
  props: {
    // The patient we're trying to find in the EMR (for context + pre-fill).
    patient: {
      type: Object,
      default: null
    }
  },
  emits: ['connect'],
  data() {
    return {
      query: '',
      results: [],
      loading: false,
      error: '',
      formatUtils
    };
  },
  computed: {
    patientName() {
      if (!this.patient) return '';
      return `${this.patient.firstName || ''} ${this.patient.lastName || ''}`.trim();
    }
  },
  created() {
    // Stand in for the extension's EMR responder until the real one ships.
    installEmrStub();
    // Pre-fill the search with the patient's name so the first search finds them.
    this.query = this.patientName;
    this.runSearch();
  },
  methods: {
    isExactMatch(result) {
      if (!this.patient || !this.patient.dateOfBirth || !result.dateOfBirth) return false;
      return result.dateOfBirth === this.patient.dateOfBirth;
    },
    handleQueryChange(value) {
      this.query = value;
      this.runSearch();
    },
    async runSearch() {
      this.loading = true;
      this.error = '';
      try {
        const response = await searchEmrPatients(this.query);
        this.results = response.patients || [];
      } catch (err) {
        console.error('EMR search failed:', err);
        this.error = 'Could not reach the EMR. Make sure the extension is connected.';
        this.results = [];
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.results {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.results-state {
  padding: 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.results-state.error {
  color: #b91c1c;
}

.result-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
}

.result-item:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.result-main {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.result-name {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.result-dob {
  font-size: 12px;
  color: #6b7280;
}

.result-match-tag {
  font-size: 11px;
  font-weight: 600;
  color: #047857;
  background-color: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  padding: 1px 8px;
}

.result-id {
  font-size: 11px;
  color: #9ca3af;
  font-family: monospace;
}
</style>
